import {
  OptionPanelActionRow,
  OptionPanelNavRow,
  OptionPanelRow,
  OptionPanelSection,
  Switch,
  type Omittable,
} from "@salestrak/ui";

import { ENTITY_KIND_ICON } from "#app/lib/entity-visuals";
import { DisabledReason, NO_PERMISSION } from "#app/components/menus/disabled-reason";
import { scopeIsWorkflowBound, type IssueScope } from "#app/lib/filters/issue-scope";

/**
 * The dashboard's Customize view sections — one flat Layout section. A
 * workspace dashboard leads with the Workflows row: it drills into the
 * panel's Workflows page, the ONE place its source set changes (a column on
 * the view row, committed at once — every widget re-scopes to it). Then
 * comes the Filters row (present only inside a saved-view scope with an
 * `IssueFilterProvider`): it drills into the panel's Filters page, which
 * edits the VIEW'S saved filters — the same page the list and board reach
 * from their shared Filters row — and the host AND-folds those filters into
 * every widget's data beside the toolbar chip's temporary pool. A value
 * picked here counts toward Save and lights the chip; one picked on the chip
 * is temporary and counts toward nothing. Every row here is a property of
 * the view. The
 * Auto-refresh switch renders for every viewer; the Auto-layout switch takes
 * `canEdit` because turning it on repacks the board. The fork actions are
 * presence-controlled by the HOST: "Reset to team layout" appears only while
 * the viewer holds a fork (its presence IS the personalized indicator —
 * deleting the fork returns them to the live shared board), and "Edit team
 * layout" appears only for a fork-holding writer (the temporary jump to the
 * shared board, closed by the banner's Done). Save/Discard live in the
 * toolbar's `SaveViewControl`, never here: the panel acts, the toolbar
 * commits — the same split the saved-view options panel keeps, and the one
 * control surfaces these switches alongside the staged widget edits.
 *
 * A fragment of `OptionPanelSection`s, not a panel: inside a saved-view scope
 * the host threads it into `ViewOptionsPanel sections=`, and outside one
 * (a mount with no `ViewsProvider`) `FreeToolbarChrome` renders
 * the same node in a popover behind the same gear. One content component,
 * two mounts.
 */
export function DashboardViewOptions({
  canEdit,
  scopeKind,
  autoRefresh,
  onAutoRefreshChange,
  autoLayout,
  onAutoLayoutChange,
  onOpenWorkflows,
  workflowCount = 0,
  onOpenFilters,
  filterCount = 0,
  canManageView = false,
  onResetToTeam,
  onEditTeamLayout,
}: Omittable<{
  /** The viewer may edit the board they are looking at (shared write or
   *  personal mode) — gates the layout actions, render-disabled. */
  canEdit: boolean;
  /** The host scope's kind — picks the Edit-team-layout row's entity icon. */
  scopeKind: IssueScope["kind"];
  /** The Auto-refresh switch: ON re-runs every widget's queries on a fixed
   *  cadence while the tab is visible. A property of the view, drafted here
   *  and committed with the rest of the tab; the row ignores `canEdit`,
   *  because who may draft a display option is not who may write the board.
   *  Persisted per view on the host. */
  autoRefresh: boolean;
  onAutoRefreshChange: (enabled: boolean) => void;
  /** The Auto-layout switch: ON packs the board immediately (undoable) and
   *  the grid's dense flow holds it packed through every later gesture;
   *  OFF freezes positions where they sit. Persisted per view on the host —
   *  the pack is a board edit, the flag is a display option. */
  autoLayout: boolean;
  onAutoLayoutChange: (enabled: boolean) => void;
  /** Drills into the panel's Workflows page — the workflows a WORKSPACE
   *  dashboard reads from. The host passes it only for such a view; a
   *  workflow's own dashboard reads from its workflow and has no set to
   *  edit. */
  onOpenWorkflows?: () => void;
  /** The size of that set, shown on the row. */
  workflowCount?: number;
  /** Drills into the panel's Filters page — the view's saved filters. The
   *  host passes it only inside a saved-view scope that also has an
   *  `IssueFilterProvider` (the page reads its field defs from there). */
  onOpenFilters?: () => void;
  /** The view's filter count (Simple + Advanced), shown on the row. The
   *  toolbar chip wears this PLUS its temporary pool's. */
  filterCount?: number;
  /** The viewer may write the saved view (`create.views`). Gates the
   *  Workflows row — the view's source set is written at once for everyone.
   *  The Filters row is a draft edit and stays open regardless. */
  canManageView?: boolean;
  /** DELETES the viewer's fork, returning them to the live shared board.
   *  The host passes it only while a fork exists — the row's presence is the
   *  "you're on your own copy" indicator, so it never renders disabled. */
  onResetToTeam?: () => void;
  /** The writer-only temporary jump to the shared board. The host passes it
   *  only for a fork-holding writer (forkless writers already edit the
   *  shared board). */
  onEditTeamLayout?: () => void;
}>) {
  const scopeIcon = scopeIsWorkflowBound(scopeKind)
    ? ENTITY_KIND_ICON.workflow
    : ENTITY_KIND_ICON.team;

  return (
    <OptionPanelSection>
      {/* EVERY layout row renders for every viewer — what shapes the board is
          never hidden, `canEdit` disables what would write to it. The fork
          rows below are the exception: their presence carries state. */}
      {onOpenWorkflows ? (
        // The source set leads everything: what the board is OF comes before
        // what narrows it. A property of the view row, written at once for
        // everyone, so the gate is the view right — locked, it says why.
        canManageView ? (
          <OptionPanelNavRow
            label="Workflows"
            icon={ENTITY_KIND_ICON.workflow}
            value={workflowCount > 0 ? workflowCount : undefined}
            onClick={onOpenWorkflows}
          />
        ) : (
          <DisabledReason reason={NO_PERMISSION}>
            <OptionPanelNavRow
              label="Workflows"
              icon={ENTITY_KIND_ICON.workflow}
              value={workflowCount > 0 ? workflowCount : undefined}
              disabled
              pointerEvents="none"
            />
          </DisabledReason>
        )
      ) : null}
      {onOpenFilters ? (
        // Filters come next — what feeds the board comes before how the
        // board is shaped. A draft edit of the view, so open to every viewer:
        // without the view right it is temporary (no Save control mounts).
        <OptionPanelNavRow
          label="Filters"
          icon="filter"
          value={filterCount > 0 ? filterCount : undefined}
          onClick={onOpenFilters}
        />
      ) : null}
      {/* Drafts like every other display option: `canEdit` is the right to
          write the BOARD, which is a different question from arming this
          view's refresh timer — the save control gates the commit. */}
      <OptionPanelRow label="Auto refresh" icon="reload">
        <Switch
          checked={autoRefresh}
          onCheckedChange={(checked) => onAutoRefreshChange(!!checked)}
        />
      </OptionPanelRow>
      <OptionPanelRow label="Auto layout" icon="layout-grid" disabled={!canEdit}>
        <Switch
          checked={autoLayout}
          disabled={!canEdit}
          onCheckedChange={(checked) => onAutoLayoutChange(!!checked)}
        />
      </OptionPanelRow>
      {onResetToTeam ? (
        <OptionPanelActionRow
          label="Reset to team layout"
          icon="reload"
          onClick={onResetToTeam}
        />
      ) : null}
      {onEditTeamLayout ? (
        <OptionPanelActionRow
          label="Edit team layout"
          icon={scopeIcon}
          onClick={onEditTeamLayout}
        />
      ) : null}
    </OptionPanelSection>
  );
}
